// CustomSideNav.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useMediaQuery,
  List,
  ListItemIcon,
  Divider,
  Tooltip,
  ListItemButton,
  Skeleton,
} from "@mui/material";
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import { attendanceIcon, careIqIcon, communicationIcon, homeIcon, InventoryIcon, logsIcon, messagingIcon, NavArrowIcon, onboard, onboardIcon, patientIcon, Reports, roomsIcon, RPMIcon, schedulerIcon, signInIcon, taskIcon, userIcon, waitingRoom, webInquiryIcon } from "./svgslist";
import { clearPrivileges } from "../reduxtoolkit/userPrivileges";
import uiConfig from "../utils/previlagecodes";
import { flag } from "../landingscreen/landingsvgs";
import FlagModal from "../landingscreen/modals/flagmodal";
import { useRecording } from "../context/RecordingContext";
import { reloadUnlessBusy } from "../utils/deferredNavigation";
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import useStore from "../store/zustandstore";
import { bsAccessOnly, navItemShown, practiceFeatures, roomsAdmin } from "../config/practice";
import { useBsAccess } from "../utils/bsAccess";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import useInboxCount from "../forms/staff/useInboxCount";
const WIDTH_EXPANDED = 240;
const WIDTH_COLLAPSED = 68;
const ITEM_HEIGHT = 40;
const HEADER_H = 65;

const CustomSideNav = ({ isCollapsed, setIsCollapsed }) => {
  const { isGlobalRecording } = useRecording();
  // Unread conversations on the Communication item (call notes, 7825; the
  // header's dbscounts "messages" count, which it puts in the store).
  const storeMsgCount = useStore((state) => state.msgcount);
  const unreadBadge = practiceFeatures().callNotes ? Number(storeMsgCount) || 0 : 0;
  // OSA batch 5 (7825): OSA's own nav entries -- Messaging (which then carries
  // the unread badge, as in OSA), its Logs icon, Online Inquiries.
  const osaNav = practiceFeatures();
  const badgeLabel = osaNav.messagingPage ? "Messaging" : "Communication";
  // OSA batch 6 (7825): the Rooms page, and the BlinkScribe pages only for a
  // user with BlinkScribe in the practice ("yes" everywhere else).
  const roomsPage = roomsAdmin();
  const bsAccess = useBsAccess();
  const privileges = useSelector((state) => state.userPrivileges.privileges);
    const dispatch = useDispatch();
  const miniSidenav = isCollapsed;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const location = useLocation();
  // Form Wizard: the Form Inbox item's badge (unreviewed submissions; red
  // while any has a critical flag). null = no badge (404, practice off).
  const formWizardNav = practiceFeatures().formWizard;
  const formInboxCount = useInboxCount(formWizardNav);
  const formInboxBadge = formInboxCount ? formInboxCount.unreviewed : 0;
  const formInboxCritical = Boolean(formInboxCount && formInboxCount.critical_unreviewed > 0);
  const [supportmodal,setSupportModal]=useState(false)
const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
const allowToken = queryParams.get('allowtoken');
const sbParam = queryParams.get('sb');
let allowedCodes = [];
if (allowToken && sbParam) {
  allowedCodes = sbParam.split(','); // split multiple codes
}
  // Phones always get the rail; on wider screens it stays as the user left it
  // (collapsed by default, see sidenavlayout).
  useEffect(() => {
    if (isMobile) setIsCollapsed(true);
  }, [isMobile, setIsCollapsed]);

  const handleCollapse = () => setIsCollapsed((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem("auth");
    dispatch(clearPrivileges());
    navigate("/sign-in/");
  };

  // ✅ Give each item a path so we can mark it active
  const navItems = [
    {
      label: "Home",
      privilege: "",
      icon: homeIcon,
      path: "/landing",
      onClick: () => {
        navigate("/landing");
        if (!isGlobalRecording) reloadUnlessBusy(); // not while a note processes or a voice recording runs
      },
    },
    {
      label: "Sign-in",
      privilege: "SignIn Sheet",
      icon: signInIcon,
      path: "/signtab",
      onClick: () => navigate("/signtab"),
    },
    ...(osaNav.messagingPage
      ? [
          {
            label: "Messaging",
            privilege: "",
            icon: messagingIcon,
            path: "/messaging",
            onClick: () => navigate("/messaging"),
          },
        ]
      : []),
    {
      label: "Note Review",
      privilege: "",
      icon: <><FactCheckOutlinedIcon /></>,
      path: "/note-review",
      onClick: () => navigate("/note-review"),
    },
    {
      label: "Print Notes",
      privilege: "",
      icon: <><LocalPrintshopOutlinedIcon /></>,
      path: "/print-notes",
      onClick: () => navigate("/print-notes"),
    },
    {
      // Standalone questionnaire station: search patient → forms →
      // Patient View tablet hand-off (2026-09-02).
      label: "Forms",
      privilege: "",
      icon: <><AssignmentOutlinedIcon /></>,
      path: "/forms",
      onClick: () => navigate("/forms"),
    },
    ...(formWizardNav
      ? [
          {
            // Form Wizard review queue (submitted patient forms).
            label: "Form Inbox",
            privilege: "",
            icon: <><InboxOutlinedIcon /></>,
            path: "/form-inbox",
            onClick: () => navigate("/form-inbox"),
          },
        ]
      : []),
    {
      label: "Communication",
      privilege: "Communication",
      icon: communicationIcon,
      path: "/fax",
      onClick: () => navigate("/fax"),
    },
    {
      label: "Attendance",
      privilege: "Attendence",
      icon: attendanceIcon,
      path: "/timesheets",
      onClick: () => navigate("/timesheets"),
    },
    {
      label: "Onboard",
      privilege: "Onboarding",
      icon: onboard,
      path: "/onboarding",
      onClick: () => navigate("/onboarding"),
    },
    // {
    //   label: "Patient",
    //   privilege: "",
    //   icon: patientIcon,
    //   path: "/patient",
    //   onClick: () => navigate("/patient"),
    // },
    {
      label: "RPM",
      privilege: "RPM",
      icon: RPMIcon,
      path: "/rpm",
      onClick: () => navigate("/rpm"),
    },
    {
      label: "User",
      privilege: "Users",
      icon: userIcon,
      path: "/user",
      onClick: () => navigate("/user"),
    },
    ...(roomsPage
      ? [
          {
            label: "Rooms",
            privilege: roomsPage.privilege || "",
            icon: roomsIcon,
            path: "/rooms",
            onClick: () => navigate("/rooms"),
          },
        ]
      : []),
    // {
    //   label: "Waiting Room",
    //   privilege: "",
    //   icon: waitingRoom,
    //   path: "/waiting-room",
    //   onClick: () => navigate("/waiting-room"),
    // },
    {
      label: "Reports",
      privilege: "Reports",
      icon: Reports,
      path: "/reports",
      onClick: () => navigate("/reports"),
    },

    // {
    //   label: "Tasks",
    //   privilege: "Tasks",
    //   icon: taskIcon,
    //   path: "/tasks",
    //   onClick: () => navigate("/tasks"),
    // },
    {
      label: "Scheduler",
      privilege: "Scheduler",
      icon: schedulerIcon,
      path: "/schedule",
      onClick: () => navigate("/schedule"),
    },
    {
      label: "Inventory",
      privilege: "Inventory",
      icon: InventoryIcon,
      path: "/inventory",
      onClick: () => navigate("/inventory"),
    },
    {
      label: "Logs",
      privilege: "Logs",
      icon: osaNav.vapiCallLogs ? logsIcon : <><PhoneAndroidIcon/></> ,
      path: "/logs",
      onClick: () => navigate("/logs"),
    },
    ...(osaNav.webInquiry
      ? [
          {
            label: "Online Inquiries",
            privilege: "Website Inquiry",
            icon: webInquiryIcon,
            path: "/webinquiry",
            onClick: () => navigate("/webinquiry"),
          },
        ]
      : []),
    {
      label: "Prices",
      privilege: "Prices",
      icon:<><PaymentIcon/></> ,
      path: "/prices",
      onClick: () => navigate("/prices"),
    },

    // {
    //   label: "CareIq",
    //   privilege: "",
    //   icon: careIqIcon,
    //   path: "/careiq",
    //   onClick: () => navigate("/careiq"),
    // },
    {
      label: "Colour Coding",
      privilege: "Colorcode",
      icon: <ColorLensOutlinedIcon />,
      path: "/colorcode",
      onClick: () => navigate("/colorcode"),
    },
  
    
    {
      label: "Support",
      privilege: "",
      icon: flag,
      path: "/log",
    onClick: () => {
      setSupportModal(true)
      }
    },
    {
      label: "Logout",
      privilege: "",
      icon: <LogoutOutlinedIcon />,
      path: "/logout",
      onClick: handleLogout,
    },
  ];

  // const filteredNavItems = navItems.filter(
  //   (i) => !i.privilege || privileges.includes(i.privilege)
  // );
  // The practice's nav allowlist first (7825: OSA's own entries; 9928 has no
  // list), so a privilege never brings back an entry the practice hides; then
  // the BlinkScribe-only entries need access.
  const practiceNavItems = navItems.filter(
    (item) => navItemShown(item.label) && (!bsAccessOnly(item.label) || bsAccess === "yes"),
  );
  const filteredNavItems = practiceNavItems.filter((item) => {
  // normal flow: no allowtoken
  if (!allowToken) {
    return !item.privilege || privileges.includes(item.privilege);
  }

  // special flow: allowtoken + sb param
  if (allowToken && allowedCodes.length > 0) {
    const configItem = uiConfig.find(cfg => cfg.item === 'sidebar');
    if (!configItem) return false;

    const matched = configItem.items.find(
      (i) => i.label === item.label && allowedCodes.includes(i.code)
    );
    return Boolean(matched);
  }

  // allowtoken present but no sb param → hide all
  return false;
});
const bottomCodes = ['SUP',"HCC", "HLO",]; // codes for Colour Coding and Logout
const bottomItems = filteredNavItems.filter((item) =>
  bottomCodes.includes(uiConfig[0].items.find((i) => i.label === item.label)?.code)
);

const topItems = filteredNavItems.filter((item) => !bottomItems.includes(item));


  const Container = ({ children }) => (
    <div
      id="custom-sidenav"
      className="fixed z-40 left-0 rounded-b-xl shadow-[0_14px_42px_rgba(0,0,0,0.06)] bg-[#FAFAF9] flex flex-col items-stretch"
      style={{
        top: HEADER_H,
        width: miniSidenav ? WIDTH_COLLAPSED : WIDTH_EXPANDED,
        transition: "width 200ms ease",
        borderTopRightRadius: 14,
        borderBottomRightRadius: 14,
        overflow: "visible", // ✅ was "clip"
        overscrollBehavior: "none",
        touchAction: "none",
        height: `calc(100% - ${HEADER_H}px)`,
      }}
    >
      <style>{`
        #custom-sidenav { height: calc(100vh - ${HEADER_H}px); }
        @supports (height: 100dvh) { #custom-sidenav { height: calc(100dvh - ${HEADER_H}px); } }
        @supports (height: 100svh) { #custom-sidenav { height: calc(100svh - ${HEADER_H}px); } }
        #custom-sidenav *::-webkit-scrollbar { display: none; width: 0; height: 0; }
      `}</style>
      {children}
    </div>
  );

  const ToggleButton = () => (
    <button
      type="button"
      aria-label="Toggle sidebar"
      onClick={(e) => {
        e.stopPropagation();
        handleCollapse();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      className="flex items-center justify-center rounded-md"
      style={{
        // In normal flow above the Home item rather than absolutely positioned.
        // It used to hang outside the rail (right:-12, zIndex 50), which painted
        // it over whatever each page rendered first; moving it inside the rail
        // instead put it on top of the Home button when collapsed. In flow it
        // can overlap neither.
        alignSelf: miniSidenav ? "center" : "flex-end",
        marginRight: miniSidenav ? 0 : 10,
        marginBottom: 8,
        flex: "none",
        outline: "none",
        border: "none",
        touchAction: "manipulation",
        pointerEvents: "auto",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          transition: "transform 200ms",
          transform: miniSidenav ? "rotate(180deg)" : "rotate(0deg)",
        }}
      >
        {NavArrowIcon}
      </div>
    </button>
  );

  const Item = ({ icon, label, onClick, path }) => {
    const location = useLocation(); // 👈 add this at top of file
    const isActive =
      path === "/logout"
        ? false
        : location.pathname === path ||
          location.pathname.startsWith(path + "/");

    const content = (
      <ListItemButton
        onClick={onClick}
        disableRipple
        sx={{
          borderRadius: miniSidenav ? "50%" : 28,
          height: ITEM_HEIGHT,
          px: miniSidenav ? 0 : 1,
          mx: miniSidenav ? 1 : 1,
          my: 0.5,
          ...(isActive
            ? {
                backgroundColor: miniSidenav ? "transparent" : "#1D2951",
                "&:hover": {
                  backgroundColor: miniSidenav ? "transparent" : "#1D2951",
                },
              }
            : {
                "&:hover": { backgroundColor: "#F5F5F5" },
              }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: miniSidenav ? "auto" : 32,
            mx: miniSidenav ? "auto" : 0,
            color: isActive ? "#FFFFFF" : "#202224",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: miniSidenav ? 0 : 1,
            width: miniSidenav ? 40 : "auto",
            height: miniSidenav ? 40 : "auto",
            borderRadius: miniSidenav ? "50%" : "none",
            backgroundColor:
              miniSidenav && isActive ? "#1D2951" : "transparent",
          }}
        >
          {icon}
        </ListItemIcon>
        {label === badgeLabel && unreadBadge > 0 && miniSidenav && (
          <span
            data-testid="nav-unread-badge"
            className="absolute top-0 right-1 min-w-[17px] h-[17px] px-1 flex items-center justify-center bg-[#E17954] text-white font-bold text-[9.5px] leading-none rounded-full ring-2 ring-white pointer-events-none"
          >
            {unreadBadge}
          </span>
        )}

        {label === "Form Inbox" && formInboxBadge > 0 && miniSidenav && (
          <span
            data-testid="nav-forminbox-badge"
            className={`absolute top-0 right-1 min-w-[17px] h-[17px] px-1 flex items-center justify-center text-white font-bold text-[9.5px] leading-none rounded-full ring-2 ring-white pointer-events-none ${
              formInboxCritical ? "bg-[#B42318]" : "bg-[#E17954]"
            }`}
          >
            {formInboxBadge}
          </span>
        )}
        {!miniSidenav && (
          <span
            style={{
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              fontSize: 14,
              color: isActive ? "#FFFFFF" : "#202224",
              fontWeight: 500,
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              textRendering: "optimizeLegibility",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginLeft: 8,
            }}
          >
            {label}
          </span>
        )}
        {label === badgeLabel && unreadBadge > 0 && !miniSidenav && (
          <span
            data-testid="nav-unread-badge"
            className={`ml-auto shrink-0 inline-flex items-center justify-center min-w-[22px] px-2 py-0.5 rounded-full font-bold text-[11px] leading-none ${
              isActive ? "bg-white/20 text-white border border-white/30" : "bg-[#FFF0E6] text-[#E17954] border border-[#F7A072]/40"
            }`}
          >
            {unreadBadge}
          </span>
        )}
        {label === "Form Inbox" && formInboxBadge > 0 && !miniSidenav && (
          <span
            data-testid="nav-forminbox-badge"
            title={formInboxCritical ? "Critical flags to review" : "Forms to review"}
            className={`ml-auto shrink-0 inline-flex items-center justify-center min-w-[22px] px-2 py-0.5 rounded-full font-bold text-[11px] leading-none ${
              isActive
                ? "bg-white/20 text-white border border-white/30"
                : formInboxCritical
                ? "bg-[#FDECEB] text-[#B42318] border border-[#B42318]/40"
                : "bg-[#FFF0E6] text-[#E17954] border border-[#F7A072]/40"
            }`}
          >
            {formInboxBadge}
          </span>
        )}
      </ListItemButton>
    );

    return miniSidenav ? (
      <Tooltip title={label} placement="right">
        <div>{content}</div>
      </Tooltip>
    ) : (
      content
    );
  };

  return (
    <>
      <Container>
        <div className="pt-4" />
        {!miniSidenav && <ToggleButton />}

        <div
          className="flex-1 flex flex-col items-stretch justify-between px-2"
          style={{ minHeight: 0 }}
        >
          {miniSidenav && <ToggleButton />}
          <div className="flex-1 mt-0 overflow-y-auto" style={{ minHeight: 0 }}>
            <List
              dense
              sx={{
                py: 0,
                overflow: "visible",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              {privileges.length === 0
                ? Array.from({ length: 6 }).map((_, idx) => (
                    <div
                      key={idx}
                      style={{
                        height: ITEM_HEIGHT,
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: miniSidenav ? 8 : 16,
                        paddingRight: 8,
                      }}
                    >
                      <Skeleton variant="circular" width={20} height={20} />
                      {!miniSidenav && (
                        <Skeleton
                          variant="text"
                          width={120}
                          height={14}
                          style={{ marginLeft: 12 }}
                        />
                      )}
                    </div>
                  ))
                : topItems.map((item, i) => (
                    <Item
                      key={`top-${i}`}
                      icon={item.icon}
                      label={item.label}
                      onClick={item.onClick}
                      path={item.path}
                    />
                  ))}
            </List>
          </div>

          <div className="flex-shrink-0">
            <Divider
              sx={{
                mx: miniSidenav ? 1 : 2,
                my: 1,
                // width: "100%",
                borderColor: "#E5E5E5",
                borderWidth: "1px",
              }}
            />

            <div className="px-2 pb-4">
              <List
                dense
                sx={{
                  py: 0,
                  overflow: "visible",
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                  width: "100%",
                }}
              >
                {bottomItems.map((item, i) => (
                  <Item
                    key={`bottom-${i}`}
                    icon={item.icon}
                    label={item.label}
                    onClick={item.onClick}
                    path={item.path}
                  />
                ))}
              </List>
            </div>
          </div>
        </div>
      </Container>
<FlagModal
  open={supportmodal}
  onClose={() => setSupportModal(false)}
  onUpdate={({ text, status }) => {
    console.log("Updated flag:", text, status);
    setSupportModal(false); // close after update
  }}
/>

    </>
  );
};

export default CustomSideNav;
